import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ExternalLink, Play, Download, Filter, Calendar, Bookmark, Trash2, X, AlertTriangle, Edit3, Check, Search } from 'lucide-react';
import { formatNumber } from '../utils/formatters';
import * as XLSX from 'xlsx';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Modern Checkbox Component matching design reference
const ModernCheckbox = ({ checked, onChange, className = "" }) => (
  <button
    onClick={onChange}
    className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all duration-150 ${
      checked 
        ? 'bg-slate-800 border-slate-800' 
        : 'bg-white border-gray-300 hover:border-gray-400'
    } ${className}`}
  >
    {checked && (
      <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />
    )}
  </button>
);

function VideoTracking() {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(false); // Start false - show cached data immediately
  const [isFetching, setIsFetching] = useState(false); // Separate state for background fetch
  const [isExporting, setIsExporting] = useState(false); // Track export state
  const [exportDays, setExportDays] = useState(10); // Days selector for export (default 10)
  const [allTimestamps, setAllTimestamps] = useState([]);
  const [platformFilter, setPlatformFilter] = useState('instagram'); // Default to Instagram
  const [cacheAge, setCacheAge] = useState(null);
  
  // Tab state - "all" or "bookmarked"
  const [activeTab, setActiveTab] = useState('all');
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [total, setTotal] = useState(0);
  const pageSize = 50;
  
  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchInput, setSearchInput] = useState(''); // For controlled input
  
  // Date range filter state
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  
  // Edit mode state - controls checkbox visibility
  const [isEditMode, setIsEditMode] = useState(false);
  
  // Selection state
  const [selectedVideos, setSelectedVideos] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  
  // Delete confirmation modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleteStep, setDeleteStep] = useState(1); // 1 = first confirm, 2 = type DELETE

  useEffect(() => {
    // QUICK FIX 3: Load cached data immediately from localStorage
    const cacheKey = activeTab === 'bookmarked' ? 'videoTrackingDataBookmarked' : 'videoTrackingData';
    const cached = localStorage.getItem(cacheKey);
    if (cached && !searchQuery) {
      try {
        const { data, timestamp } = JSON.parse(cached);
        setVideos(data);
        setCacheAge(Date.now() - timestamp);
        console.log(`✅ Loaded cached data (${Math.round((Date.now() - timestamp) / 1000)}s old)`);
        
        // Extract timestamps from cached data
        extractTimestamps(data);
        
        // Show "Updating..." banner if data is stale (>2 min old)
        if (Date.now() - timestamp > 120000) {
          setIsFetching(true);
        }
      } catch (e) {
        console.error('Failed to parse cached data:', e);
      }
    } else {
      setLoading(true); // Only show full spinner if NO cached data
    }

    // Fetch fresh data in background
    fetchVideoTracking(1, activeTab === 'bookmarked', searchQuery);
  }, [activeTab, searchQuery]);

  const extractTimestamps = (videosData) => {
    const timestampSet = new Set();
    videosData.forEach(video => {
      if (video.interval_tracking) {
        video.interval_tracking.forEach(interval => {
          if (interval.timestamp) {
            timestampSet.add(interval.timestamp);
          }
        });
      }
    });
    const sortedTimestamps = Array.from(timestampSet).sort();
    setAllTimestamps(sortedTimestamps);
  };

  const fetchVideoTracking = async (page = 1, bookmarkedOnly = false, search = '') => {
    try {
      console.log(`Fetching video tracking data (page ${page}, bookmarked: ${bookmarkedOnly}, search: '${search}')...`);
      setIsFetching(true);
      
      // Clear selections when fetching new data
      setSelectedVideos([]);
      setSelectAll(false);
      
      // Add pagination and search parameters to API call
      const params = {
        page: page,
        page_size: pageSize,
        bookmarked_only: bookmarkedOnly
      };
      
      // Only add search param if there's a search query
      if (search && search.trim()) {
        params.search = search.trim();
      }
      
      const response = await axios.get(`${API}/videos/interval-tracking`, { params });
      
      // API now returns { videos: [], pagination: {} }
      const { videos: videosData, pagination } = response.data;
      console.log(`Fetched ${videosData.length} videos (page ${page}/${pagination.total_pages})`);
      
      // Update pagination state
      setCurrentPage(pagination.page);
      setTotalPages(pagination.total_pages);
      setTotal(pagination.total);
      
      // Remove duplicates by video ID
      const uniqueVideosMap = new Map();
      videosData.forEach(video => {
        if (video.id && video.content_url && !uniqueVideosMap.has(video.id)) {
          uniqueVideosMap.set(video.id, video);
        }
      });
      const uniqueVideos = Array.from(uniqueVideosMap.values());
      
      if (videosData.length !== uniqueVideos.length) {
        console.warn(`Removed ${videosData.length - uniqueVideos.length} duplicate/invalid videos`);
      }
      
      // Collect all unique timestamps
      extractTimestamps(uniqueVideos);
      
      // Update state with fresh data
      setVideos(uniqueVideos);
      
      // Cache current page data with appropriate key
      const cacheKey = bookmarkedOnly ? 'videoTrackingDataBookmarked' : 'videoTrackingData';
      localStorage.setItem(cacheKey, JSON.stringify({
        data: uniqueVideos,
        timestamp: Date.now(),
        page: page,
        pagination: pagination
      }));
      setCacheAge(0);
      
      console.log(`✅ Page ${page} loaded and cached`);
      
    } catch (error) {
      console.error('❌ Error fetching video tracking:', error);
      // Keep showing old cached data on error
    } finally {
      setLoading(false);
      setIsFetching(false);
    }
  };
  
  // Pagination handlers
  const handleNextPage = () => {
    if (currentPage < totalPages) {
      fetchVideoTracking(currentPage + 1, activeTab === 'bookmarked', searchQuery);
      window.scrollTo(0, 0); // Scroll to top
    }
  };
  
  const handlePrevPage = () => {
    if (currentPage > 1) {
      fetchVideoTracking(currentPage - 1, activeTab === 'bookmarked', searchQuery);
      window.scrollTo(0, 0);
    }
  };
  
  const handlePageJump = (page) => {
    if (page >= 1 && page <= totalPages && page !== currentPage) {
      fetchVideoTracking(page, activeTab === 'bookmarked', searchQuery);
      window.scrollTo(0, 0);
    }
  };
  
  // Tab change handler
  const handleTabChange = (tab) => {
    if (tab !== activeTab) {
      setActiveTab(tab);
      setCurrentPage(1);
      setSelectedVideos([]);
      setSelectAll(false);
      setIsEditMode(false); // Exit edit mode when switching tabs
      setSearchQuery(''); // Clear search when switching tabs
      setSearchInput('');
    }
  };
  
  // Toggle edit mode
  const toggleEditMode = () => {
    if (isEditMode) {
      // Exiting edit mode - clear selections
      setSelectedVideos([]);
      setSelectAll(false);
    }
    setIsEditMode(!isEditMode);
  };
  
  // Search handler - triggers search when user presses Enter or clicks search button
  const handleSearch = () => {
    const trimmedSearch = searchInput.trim();
    if (trimmedSearch !== searchQuery) {
      setSearchQuery(trimmedSearch);
      setCurrentPage(1); // Reset to first page on new search
      // Reset platform filter to 'all' when searching so results aren't hidden
      if (trimmedSearch) {
        setPlatformFilter('all');
      }
    }
  };
  
  // Clear search
  const clearSearch = () => {
    setSearchInput('');
    setSearchQuery('');
    setCurrentPage(1);
  };
  
  // Handle Enter key in search input
  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = new Date(timestamp);
    const time = date.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    });
    const dateStr = date.toLocaleDateString('en-US', {
      month: 'numeric',
      day: 'numeric',
      year: 'numeric'
    });
    return `${time} (${dateStr})`;
  };

  const getViewsForTimestamp = (video, timestamp) => {
    if (!video.interval_tracking) return '-';
    const interval = video.interval_tracking.find(i => i.timestamp === timestamp);
    return interval ? formatNumber(interval.views) : '-';
  };

  // Get the latest view count for a video
  const getLatestViewCount = (video) => {
    if (!video.interval_tracking || video.interval_tracking.length === 0) return '-';
    
    // Find the most recent interval (last one in the sorted array)
    const latestInterval = video.interval_tracking[video.interval_tracking.length - 1];
    return latestInterval && latestInterval.views ? formatNumber(latestInterval.views) : '-';
  };


  // Resume tracking for a single archived video
  const handleResumeVideo = async (videoId, influencerName) => {
    const confirmed = window.confirm(
      `Resume tracking for video by "${influencerName}"?\n\nThis will change its status from ARCHIVED to ACTIVE and include it in the next cron cycle.`
    );
    
    if (!confirmed) return;
    
    try {
      setIsFetching(true);
      await axios.post(`${API}/admin/videos/${videoId}/reactivate`);
      
      alert(`✅ Successfully resumed tracking for: ${influencerName}`);
      
      // Refresh the video list
      await fetchVideoTracking(currentPage, activeTab === 'bookmarked', searchQuery);
    } catch (error) {
      console.error('❌ Error reactivating video:', error);
      alert('Failed to resume tracking. Please try again.');
    } finally {
      setIsFetching(false);
    }
  };

  // Date range filtering
  const isDateRangeActive = startDate || endDate;
  
  // Checkbox handlers
  const handleSelectAll = (checked) => {
    setSelectAll(checked);
    if (checked) {
      setSelectedVideos(filteredVideos.map(v => v.id));
    } else {
      setSelectedVideos([]);
    }
  };
  
  const handleSelectVideo = (videoId, checked) => {
    if (checked) {
      setSelectedVideos([...selectedVideos, videoId]);
    } else {
      setSelectedVideos(selectedVideos.filter(id => id !== videoId));
      setSelectAll(false);
    }
  };
  
  // Bookmark selected videos
  const handleBulkBookmark = async () => {
    if (selectedVideos.length === 0) return;
    
    try {
      setIsFetching(true);
      await axios.post(`${API}/admin/videos/bulk-bookmark`, {
        video_ids: selectedVideos,
        bookmark: true
      });
      
      alert(`✅ Successfully bookmarked ${selectedVideos.length} video(s)`);
      
      // Clear selection and refresh
      setSelectedVideos([]);
      setSelectAll(false);
      await fetchVideoTracking(currentPage, activeTab === 'bookmarked', searchQuery);
    } catch (error) {
      console.error('❌ Error bookmarking videos:', error);
      alert('Failed to bookmark videos. Please try again.');
    } finally {
      setIsFetching(false);
    }
  };
  
  // Open delete confirmation modal
  const openDeleteModal = () => {
    if (selectedVideos.length === 0) return;
    setDeleteStep(1);
    setDeleteConfirmText('');
    setShowDeleteModal(true);
  };
  
  // Handle delete confirmation
  const handleDeleteConfirm = async () => {
    if (deleteStep === 1) {
      // Move to step 2 (type DELETE)
      setDeleteStep(2);
      return;
    }
    
    // Step 2 - verify DELETE text and execute
    if (deleteConfirmText !== 'DELETE') {
      alert('Please type DELETE to confirm');
      return;
    }
    
    try {
      setIsFetching(true);
      await axios.post(`${API}/admin/videos/bulk-soft-delete`, {
        video_ids: selectedVideos
      });
      
      alert(`✅ Successfully deleted ${selectedVideos.length} video(s)`);
      
      // Close modal and clear selection
      setShowDeleteModal(false);
      setDeleteStep(1);
      setDeleteConfirmText('');
      setSelectedVideos([]);
      setSelectAll(false);
      await fetchVideoTracking(currentPage, activeTab === 'bookmarked', searchQuery);
    } catch (error) {
      console.error('❌ Error deleting videos:', error);
      alert('Failed to delete videos. Please try again.');
    } finally {
      setIsFetching(false);
    }
  };
  
  // Close delete modal
  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setDeleteStep(1);
    setDeleteConfirmText('');
  };
  
  // Legacy bulk delete (kept for compatibility)
  const handleBulkDelete = async () => {
    if (selectedVideos.length === 0) return;
    
    const confirmed = window.confirm(
      `Are you sure you want to permanently delete ${selectedVideos.length} video(s)?\n\nThis will also delete all associated metrics and cannot be undone.`
    );
    
    if (!confirmed) return;
    
    try {
      setIsFetching(true);
      await axios.post(`${API}/admin/videos/bulk-delete`, {
        video_ids: selectedVideos
      });
      
      alert(`✅ Successfully deleted ${selectedVideos.length} video(s)`);
      
      // Clear selection and refresh
      setSelectedVideos([]);
      setSelectAll(false);
      await fetchVideoTracking(currentPage, activeTab === 'bookmarked', searchQuery);
    } catch (error) {
      console.error('❌ Error deleting videos:', error);
      alert('Failed to delete videos. Please try again.');
    } finally {
      setIsFetching(false);
    }
  };

  const exportToExcel = async () => {
    try {
      setIsFetching(true);
      setIsExporting(true);
      console.log(`Exporting transposed CSV (Day 1, Day 2 format) for ${exportDays} days...`);
      
      // Use the transposed CSV endpoint with relative days
      const response = await axios.get(`${API}/videos/export-csv`, {
        params: {
          days: exportDays  // Export selected number of days
        },
        responseType: 'blob'  // Important: Get response as blob for file download
      });
      
      // Create a download link and trigger download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // Generate filename with current date and days
      const date = new Date().toISOString().split('T')[0];
      const daysLabel = exportDays >= 9999 ? 'all' : `${exportDays}days`;
      link.setAttribute('download', `video_metrics_${daysLabel}_${date}.csv`);
      
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      console.log(`✅ Transposed CSV (${exportDays} days) exported successfully`);
      setIsFetching(false);
      setIsExporting(false);
    } catch (error) {
      console.error('❌ Error exporting CSV:', error);
      console.error('Error details:', error.message);
      alert(`Failed to export data: ${error.message || 'Unknown error'}. Please check console for details.`);
      setIsFetching(false);
      setIsExporting(false);
    }
  };

  // Filter videos by platform
  const platformFilteredVideos = platformFilter === 'all' 
    ? videos 
    : videos.filter(v => v.platform?.toLowerCase() === platformFilter);
  
  // Apply date range filter
  const filteredVideos = isDateRangeActive 
    ? platformFilteredVideos.filter(video => {
        const goLiveDate = new Date(video.go_live_timestamp);
        const start = startDate ? new Date(startDate + 'T00:00:00') : null;
        const end = endDate ? new Date(endDate + 'T23:59:59') : null;
        
        if (start && goLiveDate < start) return false;
        if (end && goLiveDate > end) return false;
        
        return true;
      })
    : platformFilteredVideos;

  const getPlatformBadgeClass = (platform) => {
    const p = platform?.toLowerCase();
    if (p === 'instagram') return 'bg-pink-100 text-pink-700 border-pink-300';
    if (p === 'tiktok') return 'bg-purple-100 text-purple-700 border-purple-300';
    if (p === 'youtube') return 'bg-red-100 text-red-700 border-red-300';
    return 'bg-gray-100 text-gray-700 border-gray-300';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 px-8 py-6">
      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 overflow-hidden">
            {/* Modal Header */}
            <div className="bg-red-50 px-6 py-4 border-b border-red-100">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-red-100 rounded-full">
                    <AlertTriangle className="w-5 h-5 text-red-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    {deleteStep === 1 ? 'Confirm Deletion' : 'Final Confirmation'}
                  </h3>
                </div>
                <button
                  onClick={closeDeleteModal}
                  className="p-1 hover:bg-red-100 rounded-full transition-colors"
                >
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>
            </div>
            
            {/* Modal Body */}
            <div className="px-6 py-5">
              {deleteStep === 1 ? (
                <div className="space-y-4">
                  <p className="text-gray-700">
                    You are about to delete <span className="font-bold text-red-600">{selectedVideos.length}</span> video(s).
                  </p>
                  <p className="text-gray-600 text-sm">
                    These videos will be hidden from all views and reports. This action requires additional confirmation.
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  <p className="text-gray-700">
                    To confirm deletion, please type <span className="font-mono font-bold bg-gray-100 px-2 py-1 rounded">DELETE</span> below:
                  </p>
                  <input
                    type="text"
                    value={deleteConfirmText}
                    onChange={(e) => setDeleteConfirmText(e.target.value.toUpperCase())}
                    placeholder="Type DELETE to confirm"
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg text-center text-lg font-mono focus:outline-none focus:border-red-500 focus:ring-2 focus:ring-red-200"
                    autoFocus
                  />
                </div>
              )}
            </div>
            
            {/* Modal Footer */}
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-end gap-3">
              <button
                onClick={closeDeleteModal}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteConfirm}
                disabled={deleteStep === 2 && deleteConfirmText !== 'DELETE'}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  deleteStep === 2 && deleteConfirmText !== 'DELETE'
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-red-600 text-white hover:bg-red-700'
                }`}
              >
                {deleteStep === 1 ? 'Continue' : 'Delete Videos'}
              </button>
            </div>
          </div>
        </div>
      )}
      
      <div className="max-w-[1600px] mx-auto space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Video Tracking</h1>
          <p className="text-gray-600 mt-1">
            15-minute interval tracking for all videos
          </p>
        </div>
        
        {/* Tabs - All Videos | Bookmarked */}
        <div className="flex items-center gap-1 border-b border-gray-200">
          <button
            onClick={() => handleTabChange('all')}
            className={`px-4 py-2.5 text-sm font-medium transition-colors relative ${
              activeTab === 'all'
                ? 'text-blue-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            All Videos
            {activeTab === 'all' && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600"></div>
            )}
          </button>
          <button
            onClick={() => handleTabChange('bookmarked')}
            className={`px-4 py-2.5 text-sm font-medium transition-colors relative flex items-center gap-1.5 ${
              activeTab === 'bookmarked'
                ? 'text-blue-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <Bookmark className="w-4 h-4" />
            Bookmarked
            {activeTab === 'bookmarked' && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600"></div>
            )}
          </button>
        </div>

        {/* Updating Banner - Show when fetching in background */}
        {isFetching && !loading && (
          <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
            <div className="flex items-center justify-center space-x-2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
              <span className="text-blue-700 text-sm">
                Updating data{cacheAge ? ` (${Math.round(cacheAge / 1000)}s old)` : ''}...
              </span>
            </div>
          </div>
        )}

        {/* Filter Chips Section */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 px-6 py-4">
          <div className="flex items-center justify-between gap-4">
            {/* Left side - Filter Chips */}
            <div className="flex items-center gap-3">
              {/* Selection Actions - Show when in edit mode AND videos are selected */}
              {isEditMode && selectedVideos.length > 0 ? (
                <>
                  <div className="flex items-center gap-2 px-3 py-1.5 bg-slate-100 border border-slate-300 rounded-md">
                    <div className="w-4 h-4 rounded border-2 bg-slate-800 border-slate-800 flex items-center justify-center">
                      <Check className="w-3 h-3 text-white" strokeWidth={3} />
                    </div>
                    <span className="text-sm font-medium text-slate-700">{selectedVideos.length} selected</span>
                  </div>
                  
                  <button
                    onClick={handleBulkBookmark}
                    className="flex items-center gap-2 px-3 py-1.5 bg-amber-50 hover:bg-amber-100 border border-amber-300 rounded-md text-amber-700 transition-all text-sm font-medium"
                  >
                    <Bookmark className="w-4 h-4" />
                    <span>Bookmark</span>
                  </button>
                  
                  <button
                    onClick={openDeleteModal}
                    className="flex items-center gap-2 px-3 py-1.5 bg-red-50 hover:bg-red-100 border border-red-300 rounded-md text-red-700 transition-all text-sm font-medium"
                  >
                    <Trash2 className="w-4 h-4" />
                    <span>Delete</span>
                  </button>
                  
                  <button
                    onClick={() => { setSelectedVideos([]); setSelectAll(false); }}
                    className="flex items-center gap-2 px-3 py-1.5 bg-gray-50 hover:bg-gray-100 border border-gray-300 rounded-md text-gray-600 transition-all text-sm"
                  >
                    <X className="w-4 h-4" />
                    <span>Clear</span>
                  </button>
                </>
              ) : (
                <>
                  {/* Search Bar */}
                  <div className="relative flex items-center">
                    <div className="absolute left-3 text-gray-400">
                      <Search className="w-4 h-4" />
                    </div>
                    <input
                      type="text"
                      value={searchInput}
                      onChange={(e) => setSearchInput(e.target.value)}
                      onKeyDown={handleSearchKeyDown}
                      placeholder="Search video URLs..."
                      className="pl-9 pr-8 py-1.5 w-64 border border-gray-300 rounded-md text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                    {searchInput && (
                      <button
                        onClick={clearSearch}
                        className="absolute right-2 text-gray-400 hover:text-gray-600"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  
                  {/* Search Button */}
                  <button
                    onClick={handleSearch}
                    className="flex items-center gap-2 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 border border-blue-600 rounded-md text-white transition-all text-sm font-medium"
                  >
                    <Search className="w-4 h-4" />
                    <span>Search</span>
                  </button>
                  
                  {/* Active Search Indicator */}
                  {searchQuery && (
                    <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-50 border border-blue-200 rounded-md">
                      <span className="text-sm text-blue-700">
                        Searching: <span className="font-medium">&ldquo;{searchQuery}&rdquo;</span>
                      </span>
                      <button
                        onClick={clearSearch}
                        className="text-blue-600 hover:text-blue-800"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                  
                  {/* Platform Filter Chip */}
                  <div className="relative group">
                <button className="flex items-center gap-2 px-3 py-1.5 bg-white hover:bg-gray-50 border border-gray-300 rounded-md text-gray-700 transition-all text-sm">
                  <Filter className="w-4 h-4" />
                  <span className="font-normal">Filter</span>
                </button>
                {/* Dropdown */}
                <div className="hidden group-hover:block absolute top-full left-0 mt-1 w-44 bg-white border border-gray-200 rounded-lg shadow-lg z-50 overflow-hidden">
                  <div className="py-1">
                    <button
                      onClick={() => setPlatformFilter('all')}
                      className={`w-full text-left px-4 py-2 text-sm transition-colors ${
                        platformFilter === 'all' ? 'bg-blue-50 text-blue-600 font-medium' : 'text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      All Platforms
                    </button>
                    <button
                      onClick={() => setPlatformFilter('instagram')}
                      className={`w-full text-left px-4 py-2 text-sm transition-colors ${
                        platformFilter === 'instagram' ? 'bg-blue-50 text-blue-600 font-medium' : 'text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      Instagram
                    </button>
                    <button
                      onClick={() => setPlatformFilter('tiktok')}
                      className={`w-full text-left px-4 py-2 text-sm transition-colors ${
                        platformFilter === 'tiktok' ? 'bg-blue-50 text-blue-600 font-medium' : 'text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      TikTok
                    </button>
                    <button
                      onClick={() => setPlatformFilter('youtube')}
                      className={`w-full text-left px-4 py-2 text-sm transition-colors ${
                        platformFilter === 'youtube' ? 'bg-blue-50 text-blue-600 font-medium' : 'text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      YouTube
                    </button>
                  </div>
                </div>
              </div>

              {/* Date Range Chip - Click to open/close */}
              <div className="relative">
                <button 
                  onClick={() => setIsDatePickerOpen(!isDatePickerOpen)}
                  className="flex items-center gap-2 px-3 py-1.5 bg-white hover:bg-gray-50 border border-gray-300 rounded-md text-gray-700 transition-all text-sm"
                >
                  <Calendar className="w-4 h-4" />
                  <span className="font-normal">
                    {startDate || endDate ? 'Custom Range' : 'Last 24 hours'}
                  </span>
                </button>
                {/* Dropdown - Stays open when clicked */}
                {isDatePickerOpen && (
                  <div className="absolute top-full left-0 mt-1 w-72 bg-white border border-gray-200 rounded-lg shadow-lg z-50 p-4">
                    <div className="space-y-3">
                      <div>
                        <label className="text-xs font-medium text-gray-600 mb-1.5 block">Start Date</label>
                        <input
                          type="date"
                          value={startDate}
                          onChange={(e) => setStartDate(e.target.value)}
                          max={endDate || undefined}
                          className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-gray-700 text-sm focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="text-xs font-medium text-gray-600 mb-1.5 block">End Date</label>
                        <input
                          type="date"
                          value={endDate}
                          onChange={(e) => setEndDate(e.target.value)}
                          min={startDate || undefined}
                          className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-gray-700 text-sm focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                        />
                      </div>
                      <button
                        onClick={() => setIsDatePickerOpen(false)}
                        className="w-full px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-sm font-medium transition-colors"
                      >
                        Apply
                      </button>
                    </div>
                  </div>
                )}
              </div>
                </>
              )}
            </div>
            
            {/* Right side - Action Buttons */}
            <div className="flex items-center gap-2">
              <button
                onClick={toggleEditMode}
                className={`flex items-center gap-2 px-3 py-1.5 border rounded-md transition-all text-sm font-medium ${
                  isEditMode 
                    ? 'bg-blue-600 hover:bg-blue-700 border-blue-600 text-white' 
                    : 'bg-white hover:bg-gray-50 border-gray-300 text-gray-700'
                }`}
              >
                <Edit3 className="w-4 h-4" />
                <span>{isEditMode ? 'Done' : 'Edit'}</span>
              </button>
              
              {/* Days Selector for Export */}
              <select
                value={exportDays}
                onChange={(e) => setExportDays(Number(e.target.value))}
                className="px-3 py-1.5 bg-white border border-gray-300 rounded-md text-gray-700 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value={7}>7 days</option>
                <option value={10}>10 days</option>
                <option value={14}>14 days</option>
                <option value={30}>30 days</option>
                <option value={45}>45 days</option>
                <option value={60}>60 days</option>
                <option value={9999}>All days</option>
              </select>
              
              <button
                onClick={exportToExcel}
                className="flex items-center gap-2 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 border border-emerald-600 rounded-md text-white transition-all text-sm font-medium shadow-sm"
              >
                <Download className="w-4 h-4" />
                <span>Export CSV</span>
              </button>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-purple-50 border-b border-gray-200">
                <tr>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 sticky left-0 bg-purple-50 z-20" style={{minWidth: '160px', maxWidth: '160px', width: '160px', boxShadow: '1px 0 0 0 rgba(0,0,0,0.05)'}}>Video</th>
                <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700 sticky bg-purple-50 z-20" style={{left: '159px', minWidth: '110px', maxWidth: '110px', width: '110px', boxShadow: '1px 0 0 0 rgba(0,0,0,0.05)'}}>Platform</th>
                <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700 sticky bg-purple-50 z-20" style={{left: '268px', minWidth: '120px', maxWidth: '120px', width: '120px', boxShadow: '1px 0 0 0 rgba(0,0,0,0.05)'}}>Status</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 sticky bg-purple-50 z-20" style={{left: '387px', minWidth: '170px', maxWidth: '170px', width: '170px', boxShadow: '1px 0 0 0 rgba(0,0,0,0.05)'}}>Go Live Time</th>
                <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700 sticky bg-purple-50 z-20" style={{left: '556px', minWidth: '130px', maxWidth: '130px', width: '130px', boxShadow: '3px 0 4px 0 rgba(0,0,0,0.1)'}}>Latest Views</th>
                {allTimestamps.map((timestamp, idx) => (
                  <th key={idx} className="p-4 text-center text-sm font-semibold text-gray-700 bg-purple-50 min-w-[140px]">
                    {formatTimestamp(timestamp)}
                  </th>
                ))}
                {/* Select Column - Right Side (Sticky) - Only show in Edit Mode */}
                {isEditMode && (
                  <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700 sticky right-0 bg-purple-50 z-20" style={{minWidth: '70px', boxShadow: '-3px 0 4px 0 rgba(0,0,0,0.1)'}}>
                    <div className="flex items-center justify-center">
                      <ModernCheckbox
                        checked={selectAll}
                        onChange={() => handleSelectAll(!selectAll)}
                      />
                    </div>
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {filteredVideos.map((video, index) => (
                <tr key={video.id} className="border-b border-gray-200 hover:bg-gray-50">
                  <td className="px-4 py-3 sticky left-0 bg-white z-20" style={{minWidth: '160px', maxWidth: '160px', width: '160px', boxShadow: '1px 0 0 0 rgba(0,0,0,0.05)'}}>
                    <a
                      href={video.content_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center space-x-1 text-blue-600 hover:text-blue-700 text-sm"
                    >
                      <Play className="w-3 h-3 flex-shrink-0" />
                      <span className="truncate max-w-[100px]">{video.influencer_name || `Video ${index + 1}`}</span>
                      <ExternalLink className="w-3 h-3 flex-shrink-0" />
                    </a>
                  </td>
                  <td className="px-4 py-3 sticky bg-white z-20" style={{left: '159px', minWidth: '110px', maxWidth: '110px', width: '110px', boxShadow: '1px 0 0 0 rgba(0,0,0,0.05)'}}>
                    <div className="flex justify-center">
                      <span className={`px-2 py-1 rounded text-xs font-medium border ${getPlatformBadgeClass(video.platform)}`}>
                        {video.platform?.toUpperCase()}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-3 sticky bg-white z-20" style={{left: '268px', minWidth: '120px', maxWidth: '120px', width: '120px', boxShadow: '1px 0 0 0 rgba(0,0,0,0.05)'}}>
                    <div className="flex justify-center items-center">
                      {video.status === 'ARCHIVED' ? (
                        <div className="flex flex-col items-center space-y-1">
                          <span className="px-2 py-1 rounded text-xs font-medium border bg-gray-200 text-gray-800 border-gray-300">
                            Archived
                          </span>
                          <button
                            onClick={() => handleResumeVideo(video.id, video.influencer_name)}
                            className="px-2 py-0.5 rounded text-xs font-medium border bg-blue-100 text-blue-700 border-blue-300 hover:bg-blue-200 transition-colors"
                            title="Resume tracking for this video"
                          >
                            Resume
                          </button>
                        </div>
                      ) : (
                        <span className="px-2 py-1 rounded text-xs font-medium border bg-green-100 text-green-800 border-green-300">
                          Active
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-gray-700 text-xs whitespace-nowrap sticky bg-white z-20" style={{left: '387px', minWidth: '170px', maxWidth: '170px', width: '170px', boxShadow: '1px 0 0 0 rgba(0,0,0,0.05)'}}>
                    {video.go_live_timestamp ? new Date(video.go_live_timestamp).toLocaleString() : 'N/A'}
                  </td>
                  <td className="px-4 py-3 text-center text-gray-900 text-sm font-medium sticky bg-white z-20" style={{left: '556px', minWidth: '130px', maxWidth: '130px', width: '130px', boxShadow: '3px 0 4px 0 rgba(0,0,0,0.1)'}}>
                    {getLatestViewCount(video)}
                  </td>
                  {allTimestamps.map((timestamp, idx) => (
                    <td key={idx} className="p-4 text-center text-gray-700 text-sm">
                      {getViewsForTimestamp(video, timestamp)}
                    </td>
                  ))}
                  {/* Checkbox - Right Side (Sticky) - Only show in Edit Mode */}
                  {isEditMode && (
                    <td className="px-4 py-3 text-center sticky right-0 bg-white z-20" style={{minWidth: '70px', boxShadow: '-3px 0 4px 0 rgba(0,0,0,0.1)'}}>
                      <div className="flex items-center justify-center">
                        <ModernCheckbox
                          checked={selectedVideos.includes(video.id)}
                          onChange={() => handleSelectVideo(video.id, !selectedVideos.includes(video.id))}
                        />
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

          {videos.length === 0 && (
            <div className="p-8 text-center text-gray-500">
              No videos found
            </div>
          )}
          
          {/* Pagination Controls - Below Table */}
          {totalPages > 1 && (
            <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
              <span className="text-sm text-gray-500">
                Page {currentPage} of {totalPages} • {total} total videos
              </span>
              
              <div className="flex items-center space-x-1">
                <button
                  onClick={handlePrevPage}
                  disabled={currentPage === 1}
                  className="px-3 py-1.5 bg-white hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed border border-gray-300 rounded-md text-gray-700 transition-colors text-sm"
                >
                  ← Prev
                </button>
                
                <div className="flex items-center space-x-1">
                  {/* Show page numbers */}
                  {[...Array(Math.min(5, totalPages))].map((_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }
                    
                    return (
                      <button
                        key={pageNum}
                        onClick={() => handlePageJump(pageNum)}
                        className={`px-2.5 py-1.5 rounded-md transition-colors text-sm ${
                          currentPage === pageNum
                            ? 'bg-blue-600 text-white'
                            : 'bg-white hover:bg-gray-50 text-gray-700 border border-gray-300'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>
                
                <button
                  onClick={handleNextPage}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1.5 bg-white hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed border border-gray-300 rounded-md text-gray-700 transition-colors text-sm"
                >
                  Next →
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default VideoTracking;
